import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { GroupBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { TEAMS_TABLE_COLUMN_LABELS } from "@components/table/teams/teams-table-constants";
import { getSortByFromGridSortModel } from "@components/table/teams/teams-table-utils";
import { useTeamsTableColumns } from "@components/table/teams/use-teams-table-columns";
import { TEAMS_SORT_BY_MAP } from "@components/teams/teams-constants";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { TeamsSortBy } from "@custom-types/teams-types";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { useMembers } from "@hooks/use-members";
import { useTeamUtils } from "@hooks/use-team-utils";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch } from "@store/store-helper";
import { setTableType } from "@store/table/table-slice";
import { getMembersMapById } from "@utils/member-utils";
import { TableType } from "@utils/track-event/track-event-list";
import { HIDE_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useCallback, useEffect, useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** The teams that is shown in the table */
  teams: FaroCoreApiTypes.Team[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?: GroupBulkButtonComponents;

  /** Teams sort criteria */
  sortBy: TeamsSortBy;

  /** Callback to update the sort criteria */
  setSortBy?: (sortBy: TeamsSortBy) => void;
}

/** Listing all the teams of a company */
export function TeamsTable({
  companyId,
  teams,
  isLoading,
  bulkActionButtons,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { openTeamDetails } = useTeamUtils();
  const { companyMembers } = useMembers();
  const companyMembersMap = getMembersMapById(companyMembers);

  const handleColumnOrderChange = useHandleColumnOrderChange(TableType.teams);

  const columns = useTeamsTableColumns({
    companyId,
    companyMembersMap,
  });

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.teams));
  }, [dispatch]);

  /** Convert the sortBy prop to a GridSortModel for the table UI */
  const sortModel = useMemo(() => [TEAMS_SORT_BY_MAP[sortBy]], [sortBy]);

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getSortByFromGridSortModel(newModel);

      if (!setSortBy) {
        return;
      }

      setSortBy(newSortBy);
    },
    [setSortBy]
  );

  return (
    <FaroTable
      entities={teams}
      uniqueIdKey={"id"}
      isLoading={isLoading}
      columns={columns}
      bulkActionButtons={bulkActionButtons}
      rowHeight={70}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      sortTrackingSubject="Team"
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },

        [`& .MuiDataGrid-row:hover .${HIDE_ON_HOVER_CLASS}`]: {
          display: "none",
        },

        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          display: "flex",
        },

        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
      onRowClick={(team) => openTeamDetails({ teamId: team.id })}
      tableType={TableType.teams}
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          labelsMap={TEAMS_TABLE_COLUMN_LABELS}
          disabledColumns={["name"]}
          tableType={TableType.teams}
        />
      }
      onColumnOrderChange={handleColumnOrderChange}
    />
  );
}
